import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV2, NavEntryPoint } from '@retailer-platform/dashboard/routing'
import { retailerFundedMarketingRouter } from './routing/retailerFundedMarketingRouter'
import { retailerFundedMarketingAdminRouter } from './routing/retailerFundedMarketingAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { retailerFundedMarketingHomeAccessControl } from './access-control/retailerFundedMarketingHomeAccess.configuration'
import { retailerFundedMarketingSegmentationAccessControl } from './access-control/retailerFundedMarketingTargetingAccess.configuration'
import { marketingAgentCalendarUnionAccessControl } from './access-control/marketingAgentCalendarAccess.configuration'
import { retailerFundedMarketingV2Router } from './routing/retailerFundedMarketingV2Router'
import {
  retailerFundedMarketingAdminAccessControl,
  retailerFundedMarketingAdminCustomCampaignAccessControl,
} from './access-control/retailerFundedMarketingAdminAccess.configuration'
import { globalCampaignsAdminRouter } from './routing/GlobalCampaignsAdminRouter'
import { campaignsAdminRouter } from './routing/campaignsAdminRouter'
import { marketingAgentAdminRouter } from './routing/marketingAgentAdminRouter'
import { retailerFundedMarketingWholesalerAccessControl } from './access-control/retailerFundedMarketingWholesalerAccess.configuration'
import { retailerFundedMarketingPartnerScopedRouter } from './routing/retailerFundedMarketingPartnerScopedRouter'
import { retailerFundedMarketingPartnerScopedSegmentRouter } from './routing/retailerFundedMarketingPartnerScopedSegmentRouter'
import { lifecycleCrmAccessControl } from './access-control/lifecycleCrmAccess.configuration'
import { sfpCampaignAndSegmentAccessControl } from './access-control/sfpCampaignAndSegmentAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'retailer-funded-marketing',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'retailer-funded-marketing-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [retailerFundedMarketingV2Router, retailerFundedMarketingRouter],
    partnerScoped: [
      retailerFundedMarketingPartnerScopedRouter,
      retailerFundedMarketingPartnerScopedSegmentRouter,
    ],
    admin: [
      retailerFundedMarketingAdminRouter,
      globalCampaignsAdminRouter,
      campaignsAdminRouter,
      marketingAgentAdminRouter,
    ],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.Marketing,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav',
          route: 'retailer-funded-marketing-site-scoped-list',
          accessControl: retailerFundedMarketingHomeAccessControl,
          positionNavV2: 0,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },

            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            "Manage Instacart's main marketplace (eg: instacart.com) marketing campaigns for product promotions",
          labelId: 'retailerFundedMarketingDomain.marketplaceCampaigns.nav',
          route: 'campaigns-marketplace-list',
          subRoutes: [
            'partner-campaigns-create-mp',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV2: 1,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },

            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage marketing campaigns exclusively for your custom Storefront Pro platform',
          labelId: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav',
          route: 'campaigns-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV2: 2,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },

            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },

            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments specifically for Instacart marketplace (instacart.com) campaigns',
          labelId: 'retailerFundedMarketingDomain.marketplace.segmentation.nav',
          route: 'segment-marketplace-list',
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage customer segments for Storefront Pro campaigns',
          labelId: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav',
          route: 'segment-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV2: 5,
          inToolPermissions: [
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
          ],
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV2: 8,
          inToolPermissions: [
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
          ],
        },
        {
          type: 'item',
          llmDescription: 'Manage lifecycle marketing and customer relationship campaigns',
          labelId: 'retailerFundedMarketingDomain.lifecycleCrm.nav',
          route: 'partner-scoped-campaigns-lifecycle-crm',
          accessControl: lifecycleCrmAccessControl,
          positionNavV2: 1,
          inToolPermissions: [
            {
              slug: Permission.OffersToolingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingViewAccess,
            },
            {
              slug: Permission.OffersToolingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingModifyAccess,
            },
            {
              slug: Permission.OffersToolingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.OffersToolingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsManualCRMAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsView,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsView,
            },
            {
              slug: Permission.RetailerFundedMarketingCRMCampaignsModify,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingCRMCampaignsModify,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingModifyAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingAdminAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingRetailerFundedMarketingViewAccess,
            },
            {
              slug: Permission.RetailerFundedMarketingToolingPermissiveMode,
              permissionLabelId:
                'permissionAccordion.permissions.' +
                Permission.RetailerFundedMarketingToolingPermissiveMode,
            },
            {
              slug: Permission.RouletteApiAccessView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.RouletteApiAccessView,
            },
          ],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-global-campaigns-campaign-moments',
          subRoutes: [
            'app-admin-global-campaigns-campaign-moments-create',
            'app-admin-global-campaigns-campaign-moments-edit',
            'app-admin-global-campaigns-campaign-moments-view',
            'app-admin-global-campaigns-campaign-moments-view-moment',
            'campaigns-create',
            'campaigns-view',
            'retailer-funded-marketing-segmentation-view',
          ],
          labelId: 'global.campaigns.title',
          accessControl: retailerFundedMarketingAdminAccessControl,
          positionNavV2: 0,
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaigns-list',
          subRoutes: [
            'app-admin-campaigns-create',
            'app-admin-campaigns-edit',
            'app-admin-campaigns-get',
            'app-admin-campaigns-templates',
            'app-admin-retailer-funded-marketing-campaign-templates',
            'retailer-funded-marketing-site-scoped-campaign-templates',
          ],
          labelId: 'campaignManagement.customCampaign.title',
          accessControl: retailerFundedMarketingAdminCustomCampaignAccessControl,
          positionNavV2: 4,
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-marketing-agent-calendar',
          subRoutes: ['app-admin-marketing-agent-campaigns-view'],
          labelId: 'marketingAgent.title',
          accessControl: marketingAgentCalendarUnionAccessControl,
          positionNavV2: 5,
        },
      ],
    },
  ],
}

export default definition
